import React from "react";
import PropTypes from "prop-types";
import {Link} from "react-router-dom";
import {Chip, ListItemButton, ListItemText} from "@mui/material";
import StyledBadge from "../badges/StyledBadge";
import StyledTooltip from "../tooltips/StyledTooltip";
import {useSidebarLayout} from "./Context/SidebarProvider";
import UpgradeAdornment from "../UpgradeAdornment";
import SidebarIcon from "./SidebarIcon";
import {useSidebarItemStyles} from "./sidebarStyles";

const SidebarEntry = ({to, text, icon, badge, label, needUpgrade, inOverlay = false, dataTour}) => {
  const {
    currentPage,
    setCurrentPage,
    isSidebarExpanded,
    isSidebarTextVisible,
  } = useSidebarLayout();
  const isCurrentPage =
    currentPage === "reports" ? to === "/reports" : to.includes(currentPage);
  const shouldShowText = inOverlay || isSidebarTextVisible;
  const {classes} = useSidebarItemStyles(
    {isCurrentPage, inOverlay: shouldShowText},
  );

  const tooltipTitle = `${text}${badge ? ` (${badge})` : ""}`;

  const content = (
    <ListItemButton
      component={!needUpgrade ? Link : undefined}
      to={to}
      id={`sidebar-${text}`}
      data-tour={dataTour}
      onClick={() => setCurrentPage(to.slice(1))}
      className={classes.item}
      role={inOverlay ? "menuitem" : undefined}
      disableRipple
    >
      <StyledBadge badgeContent={!needUpgrade && !shouldShowText && badge}>
        <SidebarIcon icon={icon} />
      </StyledBadge>
      <StyledBadge badgeContent={!needUpgrade && shouldShowText && badge} max={999} padParent>
        <ListItemText
          primary={(
            <>
              {text}
              {label && <Chip label={label} size="small" className={classes.labelChip} />}
            </>
          )}
          className={shouldShowText ? classes.overlayItemText : classes.itemText}
          primaryTypographyProps={{variant: "body2", component: "div"}}
        />
      </StyledBadge>
      {needUpgrade && <UpgradeAdornment fontSize="small" />}
    </ListItemButton>
  );

  const wrappedContent = inOverlay || isSidebarExpanded ?
    (
      content
    ) :
    (
      <StyledTooltip title={tooltipTitle} placement="right">
        {content}
      </StyledTooltip>
    );

  return wrappedContent;
};

SidebarEntry.propTypes = {
  to: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  icon: PropTypes.element.isRequired,
  badge: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  label: PropTypes.string,
  needUpgrade: PropTypes.bool,
  inOverlay: PropTypes.bool,
  dataTour: PropTypes.string,
};

export default SidebarEntry;
